import React from 'react';
import { Auth0Context, Auth0ContextInterface, Auth0Provider, useAuth0, User } from '@auth0/auth0-react';
import { WithRequiredProperty } from '../../utils/WithRequiredProperty';
import { AUTH0_CONFIG } from './auth.config';

export type UserActions = {
  user: PanomioUser;
  getAccessTokenSilently: () => Promise<string>;
  logout: () => void;
  isLoading: Auth0ContextInterface<User>['isLoading'];
  error: Auth0ContextInterface<User>['error'];
};

type PanomioUser = WithRequiredProperty<User, 'email'>;

type NonValidatedUserActions = {
  email: string;
  verified: () => boolean;
  logout: () => void;
  isLoading: Auth0ContextInterface<User>['isLoading'];
  forceNewToken: () => Promise<void>;
};

export type RouterAuth = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null | undefined;
  login: () => Promise<void>;
  logout: () => void;
  verified: () => boolean;
  forceNewToken: () => Promise<void>;
};

// AUTH0_CONFIG.AUTHENTICATION_TOKEN is a real JWT minted by Panomio.ApiDevGateway's Auth0Mock (see
// Aspire/Panomio/Panomio.AppHost/AppHost.cs), so the dev user is read straight from its claims
// instead of a hand-maintained duplicate that could drift from what the gateway actually grants.
type DevUserClaims = {
  sub?: string;
  'https://app.panomio.com/email'?: string;
  'https://app.panomio.com/email_verified'?: boolean;
};

function decodeDevUser(token: string): User {
  const payload = token.split('.')[1];
  const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as DevUserClaims;
  return {
    sub: claims.sub,
    email: claims['https://app.panomio.com/email'],
    email_verified: claims['https://app.panomio.com/email_verified'],
  };
}

function createDevAuth0Context(token: string): Auth0ContextInterface<User> {
  return {
    isAuthenticated: true,
    isLoading: false,
    user: decodeDevUser(token),
    getAccessTokenSilently: (() => Promise.resolve(token)) as Auth0ContextInterface<User>['getAccessTokenSilently'],
    getAccessTokenWithPopup: () => Promise.resolve(token),
    getIdTokenClaims: () => Promise.resolve(undefined),
    loginWithRedirect: async () => {},
    loginWithPopup: async () => {},
    logout: () => {},
    buildAuthorizeUrl: () => Promise.resolve(''),
    buildLogoutUrl: () => '',
    handleRedirectCallback: () => Promise.resolve({ appState: undefined }),
  };
}

type Auth0ProviderProps = React.ComponentProps<typeof Auth0Provider>;

// Dev-gateway-only bypass: VITE_AUTHENTICATION_TOKEN (wired by Aspire/Panomio/Panomio.AppHost) is
// a JWT pre-signed for a fixed dev identity that Panomio.ApiDevGateway's Auth0Mock already accepts,
// so there is no point making a developer click through a real Auth0 login. Skip Auth0Provider
// entirely and seed its context with that pre-authenticated identity instead -- the token is still
// sent as a real Authorization: Bearer header and validated by the gateway like any other JWT.
function DevAuthProvider({ children }: Auth0ProviderProps) {
  return React.createElement(
    Auth0Context.Provider,
    { value: createDevAuth0Context(AUTH0_CONFIG.AUTHENTICATION_TOKEN) },
    children
  );
}

export const AuthenticatedUserProvider: React.ComponentType<Auth0ProviderProps> = AUTH0_CONFIG.AUTHENTICATION_TOKEN
  ? DevAuthProvider
  : Auth0Provider;

export function useRouterAuth(): RouterAuth {
  const auth0 = useAuth0();

  return {
    isAuthenticated: auth0.isAuthenticated,
    user: auth0.user,
    isLoading: auth0.isLoading,
    login: async () => {
      await auth0.loginWithRedirect({ redirect_uri: window.location.origin });
    },
    logout: createLogout(auth0),
    verified: () => auth0.isAuthenticated && !!auth0.user?.email_verified,
    forceNewToken: async () => {
      await auth0.getAccessTokenSilently({ ignoreCache: true, redirect_uri: window.location.origin });
    },
  };
}

export function useNonValidatedUser(): NonValidatedUserActions {
  const auth = useRouterAuth();

  return {
    email: auth.user?.email ?? '',
    isLoading: auth.isLoading,
    logout: auth.logout,
    verified: auth.verified,
    forceNewToken: auth.forceNewToken,
  };
}

export function useUser(): UserActions {
  const auth0 = useAuth0();

  if (!auth0.user) {
    throw new Error('Unable to get user');
  }

  return {
    user: hydrateUser(auth0.user),
    getAccessTokenSilently: auth0.getAccessTokenSilently,
    logout: createLogout(auth0),
    isLoading: auth0.isLoading,
    error: auth0.error,
  };
}

function hydrateUser({ email, ...props }: User): PanomioUser {
  if (!email) {
    throw new Error('User not properly registered');
  }
  return {
    ...props,
    email,
  };
}

function createLogout(auth0: Auth0ContextInterface): () => void {
  return () => auth0.logout({ returnTo: window.location.origin });
}
